<template>
  <span
    v-if="isParenthesisOpen"
    class="text-xl text-gray-500"
  >(</span>
  <UIInputSelect
    v-if="isField"
    v-model="fieldModel"
    :data="dataList"
    size="xs"
    class="w-34"
    @ready="handleReady"
  />
  <UIInputNumber
    v-if="isNumber"
    v-model="numberModel"
    size="xs"
    class="w-16"
    @ready="handleReady"
  />
  <template v-if="isParenthesisClose">
    <UIButtonIcon
      v-if="isInParentheses && isLastElement"
      size="xxs"
      variant="secondary"
      :leading="CheckIcon"
      @click="handleCloseParentheses"
    />
    <span class="text-xl text-gray-500">)</span>
  </template>
  <div
    v-if="isOperator"
    class="size-6 flex items-center justify-center rounded-full bg-gray-100 dark:bg-gray-800 p-1 text-sm"
  >
    <component :is="operatorToDisplay" />
  </div>
</template>

<script setup lang="ts">
import { computed, h } from 'vue'
import { orderBy } from 'lodash'

import { CalculatedColumnOperator } from './utils/enums'
import { GridColumn } from '@/components/UI/Grid/utils/types'
import { CalculatedColumn } from './utils/types'

import { FIELD_PREFIX, NUMBER_PREFIX } from './utils/const'

import { UIInputNumber, UIInputSelect } from '@ui/fields'
import { CheckIcon, DivideIcon, MinusIcon, PlusIcon, XMarkIcon } from '@heroicons/vue/24/outline'
import { UIButtonIcon } from '@ui/buttons'

type Props = {
  element: CalculatedColumn['expression']
  fields: GridColumn[]
  disabled?: boolean
  isInParentheses?: boolean
  isLastElement?: boolean
}

type Emits = {
  update: [element: CalculatedColumn['expression']]
  closeParentheses: []
}

const props = defineProps<Props>()
const emit = defineEmits<Emits>()

const isField = computed(() => {
  return props.element?.startsWith(FIELD_PREFIX)
})

const isNumber = computed(() => {
  return props.element?.startsWith(NUMBER_PREFIX)
})

const isOperator = computed(() => {
  return Object.values(CalculatedColumnOperator).includes(props.element as CalculatedColumnOperator)
})

const isParenthesisOpen = computed(() => {
  return props.element === '('
})

const isParenthesisClose = computed(() => {
  return props.element === ')'
})

const fieldModel = computed({
  get() {
    return props.element?.replace(FIELD_PREFIX, '')
  },
  set(value) {
    emit('update', `${FIELD_PREFIX}${value}`)
  },
})

const numberModel = computed({
  get() {
    const value = props.element?.replace(NUMBER_PREFIX, '')
    return value ? Number(value) : null
  },
  set(value) {
    emit('update', `${NUMBER_PREFIX}${value}`)
  },
})

const dataList = computed(() => {
  return orderBy(props.fields, 'caption').map(field => ({
    value: field.name,
    label: field.caption,
  }))
})

const operatorToDisplay = computed(() => {
  switch (props.element) {
    case CalculatedColumnOperator.SUM:
      return PlusIcon
    case CalculatedColumnOperator.SUBTRACT:
      return MinusIcon
    case CalculatedColumnOperator.MULTIPLY:
      return XMarkIcon
    case CalculatedColumnOperator.DIVIDE:
      return DivideIcon
    case CalculatedColumnOperator.PERCENT:
      return h('span', '%')
    default:
      return ''
  }
})

const handleCloseParentheses = () => {
  emit('closeParentheses')
}

const handleReady = (el: HTMLInputElement) => {
  el.focus()
}
</script>
